import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Calendar, MapPin, Filter } from 'lucide-react';
import { eventsAPI } from '../services/api';
import { parseEventDate } from '../utils/dateUtils';
import GlassyDateTimePicker from '../components/GlassyDateTimePicker';
import EventCard from '../components/EventCard';
import './EventsPage.css';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    startDate: searchParams.get('startDate') || '',
    location: searchParams.get('location') || ''
  });

  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'date');

  const parseLocalDate = parseEventDate;

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        // Backend returns a Spring Page<EventDto>. Normalize to array.
        const page = await eventsAPI.getAll({ page: 0, size: 100, sort: 'startTime,asc' });
        const allEvents = Array.isArray(page) ? page : (page?.content || []);

        // Start with all events
        let filteredEvents = [...allEvents];

        // Apply search filters from URL params (source of truth)
        const params = {
          search: searchParams.get('search') || '',
          category: searchParams.get('category') || '',
          startDate: searchParams.get('startDate') || '',
          location: searchParams.get('location') || ''
        };

        if (params.search) {
          const searchLower = params.search.toLowerCase();
          filteredEvents = filteredEvents.filter(event =>
            event.title.toLowerCase().includes(searchLower) ||
            event.description?.toLowerCase().includes(searchLower)
          );
        }

        if (params.category) {
          filteredEvents = filteredEvents.filter(event =>
            event.category === params.category
          );
        }

        if (params.startDate) {
          const filterDate = parseLocalDate(params.startDate);
          if (filterDate && !isNaN(filterDate.getTime())) {
            filteredEvents = filteredEvents.filter(event => {
              const eventDate = parseEventDate(event.startTime || event.startDate);
              return eventDate && eventDate.toDateString() === filterDate.toDateString();
            });
          }
        }

        // Sort events
        filteredEvents.sort((a, b) => {
          switch (sortBy) {
            case 'date':
              return (parseEventDate(a.startTime)?.getTime() || 0) - (parseEventDate(b.startTime)?.getTime() || 0);
            case 'title':
              return a.title.localeCompare(b.title);
            case 'category':
              return a.category.localeCompare(b.category);
            default:
              return (parseEventDate(a.startTime)?.getTime() || 0) - (parseEventDate(b.startTime)?.getTime() || 0);
          }
        });

        setEvents(filteredEvents);
      } catch (error) {
        console.error('Error loading events:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [searchParams, sortBy, parseLocalDate]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    if (sortBy !== 'date') params.set('sortBy', sortBy);

    setSearchParams(params);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const categories = ['Concert', 'Theater', 'Sports', 'Comedy', 'Conference', 'Festival'];

  return (
    <div className="events-page">
      <Helmet>
        <title>Find Events - TikPlat</title>
      </Helmet>

      <div className="events-hero-bg" aria-hidden="true" />

      <header className="events-header">
        <h1>Find Your Next Experience</h1>
        <p>Discover amazing events happening near you</p>
      </header>

      <section className="events-search-section">
        <div className="events-search-card">
          <div className="events-search-filters">
            <div className="events-filter-group">
              <label><Search size={14} /> Search Events</label>
              <input
                type="text"
                placeholder="Artist, event, venue..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>

            <div className="events-filter-group">
              <label><Filter size={14} /> Category</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div className="events-filter-group">
              <label><Calendar size={14} /> Date</label>
              <GlassyDateTimePicker
                mode="date"
                value={filters.startDate}
                onChange={(val) => handleFilterChange('startDate', val)}
                placeholder="Select date"
              />
            </div>

            <div className="events-filter-group">
              <label><MapPin size={14} /> Location</label>
              <input
                type="text"
                placeholder="City or venue..."
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
              />
            </div>
          </div>

          <button className="events-search-button" onClick={handleSearch}>
            <Search size={18} />
            Search
          </button>
        </div>
      </section>

      <section className="events-content">
        <div className="events-results-header">
          <div>
            <h2>Upcoming Events</h2>
            <div className="results-count">
              {loading ? 'Loading...' : `${events.length} events found`}
            </div>
          </div>

          <div className="events-sort-wrapper">
            <select
              className="events-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date">Sort by Date</option>
              <option value="title">Sort by Title</option>
              <option value="category">Sort by Category</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="events-loading">
            <div className="spinner"></div>
            <span>Finding the best events for you...</span>
          </div>
        ) : events.length === 0 ? (
          <div className="events-empty-state">
            <div className="empty-icon">🎭</div>
            <h3>No Events Found</h3>
            <p>Try adjusting your search criteria or check back later for new events.</p>
          </div>
        ) : (
          <div className="events-grid">
            {events.map(event => (
              <EventCard
                key={event.id}
                event={event}
                onClick={(id) => navigate(`/events/${id}`)}
                onBuyTicket={(id) => navigate(`/events/${id}`)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default EventsPage;
